// @auctogon/core/buyers-premium — the single, cross-app buyer's-premium
// calculator (IsomorphicService, ADR 010). Pure; prices in Money (ADR 060).
// admin-web's editor preview, bidder-web's bid-total preview and the invoice
// pricing engine (BL-2032) all consume it, so the premium a buyer is shown is
// the premium they are billed.
export * from './types';
export { calculateBuyersPremium, premiumRateAt } from './calculate';
export { premiumScheduleFromStored } from './schedule';
export type { PremiumSchedule, PremiumTier } from './schedule';
// BL-2482: the schedule's wire form (RateWire percents, MoneyWire amounts in the
// record's currency) — the ONE codec every DTO carrying a schedule uses.
export {
  isPremiumSchedule,
  premiumScheduleFromField,
  premiumScheduleFromWire,
  premiumScheduleToWire,
  storedPremiumScheduleToWire,
} from './wire';
export type { PremiumScheduleField, PremiumScheduleWire, PremiumTierWire } from './wire';
// BL-983: the ONE cascade resolver (ring → auction, after BL-991 and BL-1382
// retired the lot and house rungs) and the ONE
// summary formatter. Together they retired `isUsableBp`,
// `resolveEffectiveBuyersPremium` (both BUG-944) and
// `resolveCreateAuctionBuyersPremium` (core-admin) — three chains that each
// answered "which level applies?" differently, which is the BUG-506/619/558
// family. The usability predicate is now private to `resolve.ts` on purpose: an
// exported predicate is how a fourth divergent chain gets built.
export { resolveBuyersPremium } from './resolve';
export type { BuyersPremiumSource, ResolvedBuyersPremium, BuyersPremiumRungs } from './resolve';
export { formatBuyersPremium, formatBuyersPremiumRate } from './format';
export type { BuyersPremiumScheduleInput, FormatBuyersPremiumOptions } from './format';
// BL-1375: the ONE word for a calculation type. `buyersPremiumTypeLabel` is the
// type name on its own (the settings "Type" row, the editor's preview line);
// `BUYERS_PREMIUM_TYPE_LABELS` is the raw map the editor's radio reads so the
// control and the summary of what it saved can never disagree — which they did,
// for eleven days, after BL-1140 renamed only the radio.
export { buyersPremiumTypeLabel, BUYERS_PREMIUM_TYPE_LABELS } from './format';
// BL-988: the full-disclosure counterpart to the one-line summary — the band
// list behind the bidder's tiered info icon and the BP details modal. It is the
// single home for BUG-783's "above $<PREVIOUS tier's threshold>" rule, which
// previously lived hand-rolled in bidder-web's `TieredBreakdown`.
export { describeBuyersPremiumBands } from './format';
// BL-988: the ONE encoding of "does this lot actually charge anything?" —
// false for both an absent config and a deliberate 0% waiver, true for a 0%
// rate carrying a minimum fee (the floor still bills). Shared so the 0%-is-falsy
// trap is written once rather than at each surface that renders the empty state.
export { hasBuyersPremium } from './format';
// BUG-1347: the ONE decision of "across this auction's rings, do buyers pay
// different rates, and which rings do we list?" — shared by the bidder auction
// page and the printed Buyers Guide, which each decided it separately and had
// already drifted on what counts as the same rate. It also owns the gate that
// keeps a ring holding no visible lot out of both the row list AND the
// uniform-vs-varies vote.
export { decideRingPremiumDisclosure } from './ring-disclosure';
export type { DisclosableRing, RingPremiumDisclosure } from './ring-disclosure';
// BUG-1031: the ONE spelling of the waiver sentence. Four surfaces render it and
// each used to hand-write the string — two with a curly apostrophe, two with a
// straight one, so the same label read differently on admin and on the bidder's
// ring table. Import it; never retype it.
export { NO_PREMIUM } from './format';
